/**
 * locations-map.js — brand-neutral Locations map section (see ADR-0005).
 *
 * One embedded Google Map (keyless ?output=embed iframe, or a per-location
 * embed URL) beside the Location cards rendered server-side by the section.
 * Selecting a card swaps the iframe's src to that location; on the mobile
 * scroll-snap strip the card swiped into view auto-activates, debounced so
 * a fast flick across several cards loads only the one it settles on.
 * The active card is also the expanded one — CSS collapses hours + contact
 * actions on the rest (accordion), keyed off .is-active / aria-expanded.
 *
 * The optional search box filters cards client-side on their visible text
 * (name, address, hours). In the Theme Editor, selecting a Location block
 * shows it on the map immediately.
 */
(() => {
  if (customElements.get('locations-map')) return;

  const SWAP_DEBOUNCE_MS = 350;
  const OBSERVER_SUPPRESS_MS = 800;
  const mobileQuery = window.matchMedia('(max-width: 989px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class LocationsMap extends HTMLElement {
    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll('[data-locations-map-card]'));
      this.frame = this.querySelector('[data-locations-map-frame]');
      this.mapWrap = this.querySelector('[data-locations-map-map]');
      this.list = this.querySelector('[data-locations-map-list]');
      this.searchInput = this.querySelector('[data-locations-map-search]');
      this.noResults = this.querySelector('[data-locations-map-no-results]');
      if (!this.cards.length || !this.frame) return;

      this.activeIndex = Math.max(0, this.cards.findIndex((card) => card.classList.contains('is-active')));
      this.swapTimer = null;
      this.suppressObserverUntil = 0;

      this.cards.forEach((card, index) => {
        card.addEventListener('click', (event) => {
          if (event.target.closest('a')) return; // let the contact links act
          this.select(index, { scrollList: true });
        });
      });

      this.onFrameLoad = () => this.mapWrap.classList.remove('is-loading');
      this.frame.addEventListener('load', this.onFrameLoad);

      if (this.searchInput) {
        this.onSearch = () => this.filter();
        this.searchInput.addEventListener('input', this.onSearch);
      }

      // Mobile strip: the card snapped into view becomes the active one.
      this.observer = new IntersectionObserver((entries) => this.onIntersect(entries), {
        root: this.list,
        threshold: 0.6,
      });
      this.cards.forEach((card) => this.observer.observe(card));

      // Theme Editor: selecting a Location block shows it on the map.
      this.onBlockSelect = (event) => {
        const card = event.target.closest && event.target.closest('[data-locations-map-card]');
        if (!card || !this.contains(card)) return;
        this.select(this.cards.indexOf(card), { scrollList: true });
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      clearTimeout(this.swapTimer);
    }

    onIntersect(entries) {
      if (!mobileQuery.matches) return; // desktop list scrolling never drives the map
      if (Date.now() < this.suppressObserverUntil) return;
      const entry = entries.find((candidate) => candidate.isIntersecting);
      if (!entry) return;
      const index = this.cards.indexOf(entry.target);
      if (index !== -1 && index !== this.activeIndex) {
        this.select(index, { fromObserver: true });
      }
    }

    select(index, { scrollList = false, fromObserver = false } = {}) {
      const card = this.cards[index];
      if (!card) return;
      this.activeIndex = index;

      this.cards.forEach((candidate, candidateIndex) => {
        const isActive = candidateIndex === index;
        candidate.classList.toggle('is-active', isActive);
        const button = candidate.querySelector('[data-locations-map-select]');
        if (!button) return;
        if (isActive) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
        button.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      });

      if (scrollList) {
        // The programmatic scroll must not re-trigger the observer.
        this.suppressObserverUntil = Date.now() + OBSERVER_SUPPRESS_MS;
        card.scrollIntoView({
          behavior: reducedMotion.matches ? 'auto' : 'smooth',
          block: 'nearest',
          inline: mobileQuery.matches ? 'center' : 'nearest',
        });
      }

      clearTimeout(this.swapTimer);
      const delay = fromObserver ? SWAP_DEBOUNCE_MS : 0;
      this.swapTimer = setTimeout(() => this.swapMap(card), delay);
    }

    swapMap(card) {
      const src = card.getAttribute('data-map-src');
      if (!src || this.frame.getAttribute('src') === src) return;
      this.mapWrap.classList.add('is-loading');
      this.frame.setAttribute('title', card.getAttribute('data-map-title') || '');
      this.frame.setAttribute('src', src);
    }

    filter() {
      const query = this.searchInput.value.trim().toLowerCase();
      let visible = 0;
      this.cards.forEach((card) => {
        if (!card.dataset.searchText) {
          card.dataset.searchText = card.textContent.toLowerCase().replace(/\s+/g, ' ');
        }
        const matches = !query || card.dataset.searchText.includes(query);
        card.classList.toggle('is-filtered-out', !matches);
        if (matches) visible += 1;
      });
      if (this.noResults) this.noResults.hidden = visible > 0;
    }
  }

  customElements.define('locations-map', LocationsMap);
})();
